import "./OwnerChangeOrdersSection.scss";
import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import React, { useState, useEffect, useCallback, useRef } from "react";
import useScrollToTop from "hooks/useScrollToTop";
import Button from "Components/Common/Button/Button";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import CoLogTable from "Pages/GcPrecon/ChangeOrder/components/CoLogTable";
import SummaryStrip from "Pages/GcPrecon/ChangeOrder/components/SummaryStrip";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import { get, post } from "../../../utils/api/apiCaller";
import { ENDPOINTS } from "../../../utils/api/endpoints";
import Table, { type Column } from "Components/Table/Table";

import {
  LogRow,
  SummaryItem,
  TimelineItem,
} from "Pages/GcPrecon/ChangeOrder/constants/changeOrderData";
import EmptyChangeOrderIcon from "Assets/Img/EmptyChangeOrderIcon";
import EmptyState from "Components/Common/EmptyState";
import ChangeOrderDetailsView, {
  type CODetailData,
} from "Pages/Bank/BankDetails/ChangeOrderDetailsView";
import {
  CO_STATUS,
  CO_STATUS_LABELS,
  normalizeChangeOrderStatus,
} from "Pages/GcPrecon/ChangeOrder/constants/changeOrderStatus";

// ── API types ─────────────────────────────────────────────────────────────────

interface RawCOAmount {
  amount?: number;
  currency?: { symbol?: string; code?: string };
}

interface RawCODocument {
  id?: string;
  title?: string;
  name?: string;
  fileName?: string;
  key?: string;
  document?: { fileName?: string; url?: string };
}

interface RawLienItem {
  id?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  unitCost?: RawCOAmount;
  amount?: RawCOAmount;

  displayOrder?: number;

  aiaItem?: {
    id?: string;
    itemCode?: number;
    itemName?: string;
    itemLabel?: string;
  };

  subContractor?: {
    id?: string;
    companyName?: string;
  } | null;
}

interface RawCOActivity {
  title: string;
  description?: string;
  meta?: { companyName?: string; date?: string; days?: number };
}

interface RawCO {
  id: string;
  number?: string;
  coNumber?: string;
  title?: string;
  description?: string;
  date?: string;
  cause?: string;
  tradeDivisions?: string;
  status?: string;
  amount?: RawCOAmount;
  changeAmount?: RawCOAmount;
  totalAmount?: RawCOAmount;
  extendedDays?: number;
  scheduleImpact?: number | string;
  scheduleImpactDays?: number;
  projectName?: string;
  publishedByCompany?: string;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
  approvedAt?: string;
  lenderApprovedAt?: string;
  lenderRejectedAt?: string;
  gc?: { companyName?: string };
  contractor?: { companyName?: string };
  project?: { name?: string };
  documents?: RawCODocument[];
  lienItems?: RawLienItem[];
  activities?: RawCOActivity[];
  notes?: string;
}

interface COListResponse {
  data?: RawCO[];
  addOns?: {
    originalContractValue?: RawCOAmount;
    approvedCoSum?: { amount?: RawCOAmount; count?: number };
    pendingCoSum?: { amount?: RawCOAmount; count?: number };
    revisedContractValue?: RawCOAmount;
    revisedContractValueWithPendingCO?: RawCOAmount;
    totalCoCount?: number;
  };
}

type CoAction = "approved" | "rejected" | "change_request";

// Exact status strings the backend returns — these differ from what was assumed
const S_PENDING = CO_STATUS.PENDING;
const S_APPROVED = CO_STATUS.APPROVED;
const S_REJECTED = CO_STATUS.REJECTED;

// ── Helpers ───────────────────────────────────────────────────────────────────

// The backend renamed the "approved" status value when Change Order approval
// moved from Lender to Owner. Normalize any change order still carrying the
// old value so every status comparison below only has to know one value.
function normalizeCoStatus(co: RawCO): RawCO {
  const status = normalizeChangeOrderStatus(co.status);
  return status === co.status ? co : { ...co, status };
}

function getSymbol(raw?: RawCOAmount): string {
  return raw?.currency?.symbol ?? "$";
}

function formatMoney(raw?: RawCOAmount): string {
  if (!raw || raw.amount == null) return "—";
  const sym = getSymbol(raw);
  return `${sym}${Math.abs(raw.amount).toLocaleString("en-US")}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatScheduleImpact(val?: number | string): string {
  if (val == null || val === "" || val === 0) return "None";
  if (typeof val === "string") return val;
  return `${val > 0 ? "+" : ""}${val} day${Math.abs(val) !== 1 ? "s" : ""}`;
}

function getAmount(co: RawCO): RawCOAmount | undefined {
  return co.totalAmount ?? co.amount ?? co.changeAmount;
}

function buildTimeline(co: RawCO): TimelineItem[] {
  if (co.activities && co.activities.length > 0) {
    return co.activities.map((act, i) => ({
      id: i + 1,
      status:
        act.title.toLowerCase().includes("awaiting bank approval") ||
        act.title.toLowerCase().includes("awaiting owner approval")
          ? "pending"
          : "done",
      title: act.title,
      meta:
        [act.meta?.companyName, formatDate(act.meta?.date)]
          .filter(Boolean)
          .join(" — ") ||
        act.description ||
        "",
    }));
  }
  // Fallback when detail not yet loaded
  const isPending = co.status === S_PENDING;
  const gcName =
    co.publishedByCompany ??
    co.gc?.companyName ??
    co.contractor?.companyName ??
    "GC";
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return [
    {
      id: 1,
      status: "done",
      title: "Submitted by GC",
      meta: `${gcName} — ${formatDate(submittedDate)}`,
    },
    {
      id: 2,
      status: "done",
      title: "CSC New England notified",
      meta: "CSC notified upon submission",
    },
    {
      id: 3,
      status: isPending ? "pending" : "done",
      title: isPending ? "Awaiting Owner Approval" : "Decision recorded",
      meta: isPending
        ? "Pending your review"
        : (co.lenderApprovedAt ?? co.approvedAt)
          ? `Resolved ${formatDate(co.lenderApprovedAt ?? co.approvedAt)}`
          : "Resolved",
    },
  ];
}

function mapToLogRow(co: RawCO): LogRow {
  const amt = getAmount(co);
  const sym = getSymbol(amt);
  const num = amt?.amount;
  const amountStr =
    num != null ? `+${sym}${Math.abs(num).toLocaleString("en-US")}` : "—";
  const s = CO_STATUS_LABELS[co.status ?? ""] ?? {
    label: co.status ?? "—",
    cls: "bPending",
  };
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return {
    id: co.number ?? co.coNumber ?? co.id,
    dbId: co.id,
    description: co.title ?? "—",
    sub: formatDate(submittedDate),
    submitted: formatDate(submittedDate),
    amount: amountStr,
    amountTone: "pos",
    daysImpact: formatScheduleImpact(
      co.extendedDays ?? co.scheduleImpact ?? co.scheduleImpactDays,
    ),
    status: s.label,
    statusClass: s.cls,
  };
}

// const ifAllApprovedDelta =
//   addOns?.pendingCoSum?.amount + addOns?.approvedCoSum?.amount;
function buildSummary(addOns?: COListResponse["addOns"]): SummaryItem[] {
  const approvedAmount = addOns?.approvedCoSum?.amount?.amount ?? 0;
  const pendingAmount = addOns?.pendingCoSum?.amount?.amount ?? 0;

  const ifAllApprovedAmount: RawCOAmount = {
    amount: approvedAmount + pendingAmount,
    currency:
      addOns?.approvedCoSum?.amount?.currency ??
      addOns?.pendingCoSum?.amount?.currency,
  };
  return [
    {
      label: "Original Contract",
      value: formatMoney(addOns?.originalContractValue),
    },
    {
      label: "Approved COs",
      value: formatMoney(addOns?.approvedCoSum?.amount),
      tone: "up",
      sub:
        addOns?.approvedCoSum?.count != null
          ? `${addOns.approvedCoSum.count} approved`
          : undefined,
    },
    {
      label: "Pending Approval",
      value: formatMoney(addOns?.pendingCoSum?.amount),
      tone: "blue",
      sub:
        addOns?.pendingCoSum?.count != null
          ? `${addOns.pendingCoSum.count} pending`
          : undefined,
    },
    {
      label: "Revised Contract",
      value: formatMoney(addOns?.revisedContractValue),
      sub: "Approved only",
    },
    {
      label: "If All Approved",
      value: formatMoney(addOns?.revisedContractValueWithPendingCO),
      tone: "up",
      sub: `+${formatMoney(ifAllApprovedAmount)}`,
    },
  ];
}

const CONFIRMATION_TITLES: Record<CoAction, string> = {
  approved: "Approve Change Order",
  rejected: "Reject Change Order",
  change_request: "Request More Information",
};

// ── Component ─────────────────────────────────────────────────────────────────

interface OwnerChangeOrdersSectionProps {
  projectId: string;
}

export default function OwnerChangeOrdersSection({
  projectId,
}: OwnerChangeOrdersSectionProps) {
  const [pendingCOs, setPendingCOs] = useState<RawCO[]>([]);
  const [approvedCOs, setApprovedCOs] = useState<RawCO[]>([]);
  const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([]);
  const [revisedContractValue, setRevisedContractValue] = useState<
    RawCOAmount | undefined
  >(undefined);
  const [activeCO, setActiveCO] = useState<RawCO | null>(null);
  const [activeCODetail, setActiveCODetail] = useState<RawCO | null>(null);
  const [viewedApprovedCO, setViewedApprovedCO] = useState<RawCO | null>(null);
  const [viewedApprovedCODetail, setViewedApprovedCODetail] =
    useState<RawCO | null>(null);
  const [viewedDetailLoading, setViewedDetailLoading] = useState(false);
  useScrollToTop(viewedApprovedCO?.id ?? "");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [submitting, setSubmitting] = useState<CoAction | null>(null);
  const [actionMsg, setActionMsg] = useState("");
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<CoAction | null>(null);

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openConfirmationModal = (action: CoAction) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setSelectedAction(action);
    setActionNote("");
    setIsConfirmModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmModalOpen(false);
    closeTimeoutRef.current = setTimeout(() => {
      setSelectedAction(null);
      closeTimeoutRef.current = null;
    }, 300);
  };

  const confirmAction = async () => {
    if (!selectedAction) return;
    closeConfirmationModal();
    await handleAction(selectedAction);
  };

  const fetchCOs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");

    const mainEndpoint = filterStatus
      ? ENDPOINTS.CHANGE_ORDERS.LENDER_LIST(projectId, filterStatus)
      : ENDPOINTS.CHANGE_ORDERS.LENDER_LIST(projectId);

    const mainRes = (await get(mainEndpoint)) as {
      data: COListResponse | null;
      error: { message?: string } | null;
    };

    setLoading(false);

    if (mainRes.error) {
      setError(mainRes.error.message ?? "Failed to load change orders.");
      return;
    }

    const all = (mainRes.data?.data ?? []).map(normalizeCoStatus);
    const pending = all.filter((co) => co.status === S_PENDING);
    const approved = all.filter((co) => co.status !== S_PENDING);

    const nextActiveCO = pending[0] ?? null;
    setPendingCOs(pending);
    setApprovedCOs(approved);
    setActiveCO(nextActiveCO);
    if (!nextActiveCO) setActiveCODetail(null);
    setSummaryItems(buildSummary(mainRes.data?.addOns));
    setRevisedContractValue(mainRes.data?.addOns?.revisedContractValue);
  }, [projectId, filterStatus]);

  const handleFilterChange = (status: string | null) => {
    setFilterStatus(status);
    setActiveCO(null);
    setActiveCODetail(null);
    setActionMsg("");
    setActionNote("");
  };

  useEffect(() => {
    fetchCOs();
  }, [fetchCOs]);

  useEffect(() => {
    if (!activeCO?.id || !projectId) return;
    let cancelled = false;
    setDetailLoading(true);
    setActiveCODetail(null);

    (async () => {
      const { data, error: apiError } = (await get(
        ENDPOINTS.CHANGE_ORDERS.LENDER_DETAIL(projectId, activeCO.id),
      )) as {
        data: { data?: RawCO } | null;
        error: { message?: string } | null;
      };

      if (cancelled) return;
      setDetailLoading(false);
      if (!apiError && data?.data)
        setActiveCODetail(normalizeCoStatus(data.data));
    })();

    return () => {
      cancelled = true;
    };
  }, [activeCO?.id, projectId]);

  // ── Fetch detail for viewed approved CO ──────────────────────────────────────

  useEffect(() => {
    if (!viewedApprovedCO?.id || !projectId) return;
    let cancelled = false;
    setViewedDetailLoading(true);
    setViewedApprovedCODetail(null);

    (async () => {
      const { data, error: apiError } = (await get(
        ENDPOINTS.CHANGE_ORDERS.LENDER_DETAIL(projectId, viewedApprovedCO.id),
      )) as {
        data: { data?: RawCO } | null;
        error: { message?: string } | null;
      };

      if (cancelled) return;
      setViewedDetailLoading(false);
      if (!apiError && data?.data)
        setViewedApprovedCODetail(normalizeCoStatus(data.data));
    })();

    return () => {
      cancelled = true;
    };
  }, [viewedApprovedCO?.id, projectId]);

  const handleAction = async (action: CoAction) => {
    if (!activeCO || !projectId) return;
    setSubmitting(action);
    setActionMsg("");

    const { error: apiError } = (await post(
      ENDPOINTS.CHANGE_ORDERS.OWNER_ACTION(projectId, activeCO.id),
      { action, description: actionNote },
    )) as { data: unknown; error: { message?: string } | null };

    if (apiError) {
      setSubmitting(null);
      setActionMsg(apiError.message ?? "Action failed. Please try again.");
      return;
    }

    // before fetchCOs resolves and picks the next pending CO.
    setActiveCODetail(null);
    setActiveCO(null);
    setActionNote("");
    await fetchCOs();
    setSubmitting(null);
  };

  const co = activeCODetail ?? activeCO;
  const timeline = co ? buildTimeline(co) : [];
  const amt = co ? getAmount(co) : undefined;
  const displayAmt = co?.totalAmount;
  const amountDisplay = displayAmt ? `+${formatMoney(displayAmt)}` : "—";
  const submittedOn = co?.publishedAt ?? co?.submittedAt;
  const daysAwaiting = submittedOn
    ? Math.floor((Date.now() - new Date(submittedOn).getTime()) / 86_400_000)
    : null;
  const coId = co?.number ?? co?.coNumber ?? co?.id ?? "";
  const newContractTotalIfApproved = (() => {
    const revised = revisedContractValue?.amount;
    const coAmt = amt?.amount;
    if (revised == null || coAmt == null) return null;
    const sym = getSymbol(revisedContractValue);
    return `${sym}${(revised + coAmt).toLocaleString("en-US")}`;
  })();

  return (
    <section className="ownerChangeOrdersSection">
      <h1 className="sectionTitle">Change Order Review</h1>
      <p className="sectionSub">
        Pending change orders awaiting your approval. Approved COs update
        contract value immediately.
      </p>

      <SummaryStrip items={summaryItems} />

      {viewedApprovedCO ? (
        <ChangeOrderDetailsView
          co={(viewedApprovedCODetail ?? viewedApprovedCO) as CODetailData}
          loading={viewedDetailLoading}
          onClose={() => {
            setViewedApprovedCO(null);
            setViewedApprovedCODetail(null);
          }}
        />
      ) : (
        <>
          {loading && <TableSkeleton cols={5} rows={3} />}
          {error && <div className="errorState">{error}</div>}

          {!loading && !error && (
            <>
              {/* <div className="actionsInline" style={{ marginBottom: 14 }}>
            {(
              [
                { label: "All", value: null },
                { label: "Pending", value: S_PENDING },
                { label: "Approved", value: S_APPROVED },
              ] as { label: string; value: string | null }[]
            ).map(({ label, value }) => (
              <Button
                key={label}
                className={`btn btnSm ${filterStatus === value ? "btnPrimary" : "btnSecondary"}`}
                onClick={() => handleFilterChange(value)}
              >
                {label}
              </Button>
            ))}
          </div> */}

              {pendingCOs.length > 1 && (
                <div className="actionsInline" style={{ marginBottom: 12 }}>
                  {pendingCOs.map((co) => (
                    <Button
                      key={co.id}
                      className={`btn btnSm ${activeCO?.id === co.id ? "btnPrimary" : "btnSecondary"}`}
                      onClick={() => {
                        setActiveCO(co);
                        setActionMsg("");
                        setActionNote("");
                      }}
                    >
                      {co.number ?? co.coNumber ?? co.id}
                    </Button>
                  ))}
                </div>
              )}

              {co ? (
                <article className="coDetailCard">
                  <div className="coDetailHeader">
                    <div>
                      <p className="coLabel">
                        {co.status === S_APPROVED
                          ? "Change Order - Approved"
                          : co.status === S_REJECTED
                            ? "Change Order - Rejected"
                            : "Change Order - Pending Your Approval"}
                      </p>
                      <h2>
                        {co.number ?? co.number ?? "—"} — {co.title ?? "—"}
                      </h2>
                      <p className="coMeta">
                        {[
                          co.projectName ?? co.project?.name,
                          (co.publishedByCompany ??
                          co.gc?.companyName ??
                          co.contractor?.companyName)
                            ? `Submitted by ${co.publishedByCompany ?? co.gc?.companyName ?? co.contractor?.companyName}`
                            : null,
                          formatDate(co.publishedAt ?? co.submittedAt),
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </p>
                    </div>
                    <div className="coAmountBox">
                      <span>Change Amount</span>
                      <strong>{amountDisplay}</strong>
                      {co.status === S_PENDING ? (
                        <span className="badge bPending">
                          <span className="dot" />
                          Awaiting Owner Approval
                        </span>
                      ) : co.status === S_APPROVED ? (
                        <span className="badge bApproved">
                          <span className="dot" />
                          Owner Approved
                        </span>
                      ) : co.status === S_REJECTED ? (
                        <span className="badge bRejected">
                          <span className="dot" />
                          Rejected
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="impactBar">
                    <div>
                      <strong className="up">{amountDisplay}</strong>
                      <span>Contract Increase</span>
                    </div>
                    <div>
                      <strong className="amber">
                        {formatScheduleImpact(
                          co.extendedDays ??
                            co.scheduleImpact ??
                            co.scheduleImpactDays,
                        )}
                      </strong>
                      <span>Schedule Impact</span>
                    </div>
                    <div>
                      <strong>{co.documents?.length ?? 0}</strong>
                      <span>Documents Attached</span>
                    </div>
                    <div>
                      <strong>
                        {daysAwaiting != null
                          ? `${daysAwaiting} day${daysAwaiting !== 1 ? "s" : ""}`
                          : "—"}
                      </strong>
                      <span>Awaiting Review</span>
                    </div>
                  </div>

                  <div className="coBody">
                    {detailLoading ? (
                      <TableSkeleton cols={1} rows={4} />
                    ) : (
                      <>
                        <h3>Description of Change</h3>
                        <p>{co.description ?? "No description provided."}</p>

                        {(co.cause || co.tradeDivisions) && (
                          <div className="coMetaGrid">
                            {co.cause && (
                              <div>
                                <h3>Reason / Cause</h3>
                                <p>{co.cause}</p>
                              </div>
                            )}
                            {co.tradeDivisions && (
                              <div>
                                <h3>Trade Division</h3>
                                <p>{co.tradeDivisions}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {co.lienItems &&
                          co.lienItems.length > 0 &&
                          (() => {
                            const totalAmt = co.lienItems!.reduce(
                              (sum, item) => sum + (item.amount?.amount ?? 0),
                              0,
                            );
                            const columns: Column<RawLienItem>[] = [
                              {
                                header: (
                                  <>
                                    A
                                    <span className="tableSubTitle">
                                      Item #
                                    </span>
                                  </>
                                ),
                                className: "center",
                                render: (item) => item.displayOrder ?? "—",
                              },
                              {
                                header: "CSI Code",
                                render: (item) => item.aiaItem?.itemCode ?? "—",
                              },
                              {
                                header: (
                                  <>
                                    B
                                    <span className="tableSubTitle">
                                      Description of Work
                                    </span>
                                  </>
                                ),
                                render: (item) => item.description ?? "—",
                              },
                              {
                                header: (
                                  <>
                                    C
                                    <span className="tableSubTitle">
                                      Scheduled Value
                                    </span>
                                  </>
                                ),
                                className: "num",
                                render: (item) => {
                                  const amt = item.amount?.amount ?? 0;
                                  const sym =
                                    item.amount?.currency?.symbol ?? "$";
                                  return `${sym}${amt.toLocaleString("en-US")}`;
                                },
                              },
                              {
                                header: "% of CONTRACT",
                                className: "num",
                                render: (item) => {
                                  const amt = item.amount?.amount ?? 0;
                                  return totalAmt > 0
                                    ? `${((amt / totalAmt) * 100).toFixed(1)}%`
                                    : "0.0%";
                                },
                              },
                              {
                                header: "Subcontractor / Vendor",
                                render: (item) =>
                                  item.subContractor?.companyName ?? "—",
                              },
                            ];
                            const footer: React.ReactNode[] = [
                              <span key="label" className="coTableLabel">
                                Total Change Order Amount
                              </span>,
                              null,
                              null,
                              <span key="total" className="coTableAmount">
                                {formatMoney(displayAmt)}
                              </span>,
                              <span key="pct" className="muted">
                                {totalAmt > 0 ? "100.0%" : "0.0%"}
                              </span>,
                              null,
                            ];
                            return (
                              <>
                                <h3>Budget Breakdown</h3>
                                <Table<RawLienItem>
                                  data={co.lienItems!}
                                  columns={columns}
                                  footerRow={footer}
                                  tableClassName="coTable"
                                  footerRowClassName="coTableFooter"
                                />
                              </>
                            );
                          })()}

                        <h3>Attached Documents</h3>
                        {co.documents && co.documents.length > 0 ? (
                          <div className="fileChipWrap">
                            {co.documents.map((doc, i) => {
                              const label =
                                doc.title ??
                                doc.document?.fileName ??
                                doc.fileName ??
                                doc.name ??
                                doc.key ??
                                `Document ${i + 1}`;
                              const url = doc.document?.url;
                              return url ? (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fileChip"
                                >
                                  {label}
                                </a>
                              ) : (
                                <span key={i} className="fileChip">
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p>No documents attached.</p>
                        )}

                        <h3>Activity</h3>
                        <ul className="timeline">
                          {timeline.map((item) => (
                            <li key={item.id}>
                              <span className={`tlDot ${item.status}`.trim()}>
                                {item.status === "pending" ? "..." : "✓"}
                              </span>
                              <div>
                                <strong>{item.title}</strong>
                                <p>{item.meta}</p>
                              </div>
                            </li>
                          ))}
                        </ul>

                        {(co.status === S_APPROVED ||
                          co.status === S_REJECTED) && (
                          <div
                            className={`coDecisionBanner ${
                              co.status === S_APPROVED
                                ? "decisionApproved"
                                : "decisionRejected"
                            }`}
                          >
                            <span className="decisionIcon">
                              {co.status === S_APPROVED ? "✓" : "✕"}
                            </span>
                            <div className="decisionBody">
                              <strong>
                                {co.status === S_APPROVED
                                  ? "Approved by Owner"
                                  : "Rejected by Owner"}
                              </strong>
                              <p>
                                {co.status === S_APPROVED
                                  ? "This change order has been approved. The project contract value has been updated."
                                  : "This change order has been rejected. The GC has been notified and may revise and resubmit."}
                              </p>
                              {(co.lenderApprovedAt ?? co.lenderRejectedAt) && (
                                <span className="decisionDate">
                                  {formatDate(
                                    (co.status === S_APPROVED
                                      ? co.lenderApprovedAt
                                      : co.lenderRejectedAt) ?? "",
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {co.status === S_PENDING && (
                      <div className="approvalPanel">
                        <h3>
                          Your Decision on {co.number ?? co.coNumber ?? co.id}
                        </h3>
                        <p>
                          Approve or reject this change order. If approved, the
                          project contract value will be updated to{" "}
                          {newContractTotalIfApproved}. GC and CSC will be
                          notified immediately.
                        </p>
                        {actionMsg && (
                          <div
                            className={
                              actionMsg.toLowerCase().includes("fail")
                                ? "errorState"
                                : "successState"
                            }
                            style={{ marginBottom: 8 }}
                          >
                            {actionMsg}
                          </div>
                        )}
                        <div className="panelActions">
                          <Button
                            className="btn btnSecondary"
                            onClick={() =>
                              openConfirmationModal("change_request")
                            }
                            disabled={submitting !== null}
                          >
                            {submitting === "change_request"
                              ? "Sending…"
                              : "Request More Info"}
                          </Button>
                          <Button
                            className="btn btnReject"
                            onClick={() => openConfirmationModal("rejected")}
                            disabled={submitting !== null}
                          >
                            {submitting === "rejected"
                              ? "Rejecting…"
                              : "Reject"}
                          </Button>
                          <Button
                            className="btn btnApprove"
                            onClick={() => openConfirmationModal("approved")}
                            disabled={submitting !== null}
                          >
                            {submitting === "approved"
                              ? "Approving…"
                              : `Approve ${amountDisplay}`}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              ) : (
                !loading &&
                approvedCOs.length === 0 && (
                  <EmptyState
                    noBorder={true}
                    height={"100px"}
                    icon={<EmptyChangeOrderIcon />}
                    title="No Change Orders Pending Approval"
                    subtitle="There are no change orders awaiting your approval at this time."
                  />
                )
              )}
              {approvedCOs.length > 0 && (
                <>
                  <h2 className="subHeading">Change Order History</h2>
                  <CoLogTable
                    rows={approvedCOs.map((co) => {
                      const row = mapToLogRow(co);
                      const resolvedDate = co.lenderApprovedAt ?? co.approvedAt;
                      return {
                        ...row,
                        sub: resolvedDate
                          ? `${row.status} ${formatDate(resolvedDate)}`
                          : row.sub,
                      };
                    })}
                    onOpenRow={(row) => {
                      const raw = approvedCOs.find((c) => c.id === row.dbId);
                      if (raw) {
                        setViewedApprovedCO(raw);
                        setViewedApprovedCODetail(null);
                      }
                    }}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      <CustomModal
        show={isConfirmModalOpen}
        handleClose={closeConfirmationModal}
        title={selectedAction ? CONFIRMATION_TITLES[selectedAction] : ""}
        footerContent={
          selectedAction ? (
            <>
              <Button
                className="btn btnSecondary"
                onClick={closeConfirmationModal}
              >
                Cancel
              </Button>
              <Button
                className={`btn ${
                  selectedAction === "approved"
                    ? "btnApprove"
                    : selectedAction === "rejected"
                      ? "btnReject"
                      : "btnPrimary"
                }`}
                onClick={confirmAction}
                disabled={submitting !== null}
              >
                {submitting
                  ? "Processing…"
                  : selectedAction === "approved"
                    ? "Confirm Approval"
                    : selectedAction === "rejected"
                      ? "Confirm Rejection"
                      : "Send Request"}
              </Button>
            </>
          ) : null
        }
      >
        {selectedAction && (
          <>
            <p>
              {selectedAction === "approved"
                ? `You are approving ${coId}. The project contract value will be updated immediately. GC and CSC will be notified.`
                : selectedAction === "rejected"
                  ? `You are rejecting ${coId}. The GC will be notified with your reason. They may revise and resubmit.`
                  : `Request additional documentation or clarification from the GC before making a decision on ${coId}.`}
            </p>
            {co && (
              <div className="coModalSummary">
                <strong>
                  {co.number ?? co.coNumber ?? co.id} — {co.title ?? "—"}
                </strong>
                <span>
                  {"Amount: "}
                  <strong>{amountDisplay}</strong>
                  {newContractTotalIfApproved && (
                    <>
                      {" · New contract total if approved: "}
                      <strong>{newContractTotalIfApproved}</strong>
                    </>
                  )}
                </span>
              </div>
            )}
            <p className="modalFieldLabel">
              {selectedAction === "rejected"
                ? "REASON FOR REJECTION (REQUIRED)"
                : "NOTES (OPTIONAL)"}
            </p>
            <TextArea
              className="fieldTextarea"
              placeholder="Optional notes..."
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
          </>
        )}
      </CustomModal>
    </section>
  );
}
